import { useMemo, useState, type ChangeEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { apiRequest, getBootstrapData } from '@tpct/admin-ui';
import FeatureGateNotice from '@/hub-shared/components/FeatureGateNotice';
import FeatureGateTooltip from '@/hub-shared/components/FeatureGateTooltip';
import { getFecDsnImportGateFromBootstrap } from '@/hub-shared/gates/fecDsnImportGate';
import { __ } from '@/hub-shared/utils/i18n';
import type { CompanyReportsResponse } from '../../types/company-page';
import FecDsnUploadResponsibilityNotice from '../../../../companies/src/shared/FecDsnUploadResponsibilityNotice';
import ProfileArtifactFileDropzone from '../../../../companies/src/shared/ProfileArtifactFileDropzone';
import {
  COMPANY_PROFILE_ARTIFACT_FILE_ACCEPT,
  detectProfileArtifactType,
  type ProfileArtifactType,
} from '../../../../companies/src/constants/companyProfileImport';
import {
  createFileStoreApi,
  type ImportBlockedHint,
  type PrefillDivergence,
  type PrefillMergeResult,
} from '@tpct/mission-core/fileStore/fileStoreApi';
import { summarizeImportOutcome, hasHardImportBlock, labelForMetric } from './profileImportOutcome';

type ProfileArtifactImportSectionProps = {
  companyId: number;
  bootstrapKey: string;
};

type ImportPhase = 'idle' | 'uploading' | 'importing' | 'success' | 'error';

type BlockedHint = ImportBlockedHint;

type ImportEnvelope = {
  stable?: Record<string, unknown>;
  monthly_facts?: unknown[];
  annual_facts?: unknown[];
  blocked?: BlockedHint[];
};

async function readWpRestError(response: Response): Promise<{ message: string; code?: string }> {
  const raw = (await response.json().catch(() => ({}))) as { message?: string; code?: string };
  const message =
    typeof raw.message === 'string' && raw.message.length > 0 ? raw.message : `${__('Erreur')} ${response.status}`;
  const code = typeof raw.code === 'string' && raw.code.length > 0 ? raw.code : undefined;
  return { message, code };
}

function formatError(e: { message: string; code?: string }): string {
  return e.message;
}

/** Strips a leading schema-code segment (e.g. `b1p27-c-i_`) off a raw VSME field id, keeping
 *  only the trailing human-readable slug. Returns null when nothing readable is left. */
function humanizeFieldId(fieldId: string): string | null {
  const withoutCodePrefix = fieldId.replace(/^[a-z0-9]+(?:-[a-z0-9]+)*_/i, '');
  const candidate = withoutCodePrefix !== '' ? withoutCodePrefix : fieldId;
  if (candidate === fieldId && !/[_-]/.test(candidate)) {
    return null;
  }
  const words = candidate.split(/[_-]+/).filter(Boolean);
  if (words.length === 0) {
    return null;
  }
  return words.map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

/** Best-effort human label for a divergence: the known metric label, else a humanized field id,
 *  else the raw id as a last resort so the row is never blank. */
function divergenceFieldLabel(d: PrefillDivergence): string {
  if (d.metric_key) {
    return labelForMetric(d.metric_key);
  }
  const fieldId = d.field_id ?? '';
  if (fieldId === '') {
    return __('Champ inconnu');
  }
  return humanizeFieldId(fieldId) ?? `${__('Champ')} ${fieldId}`;
}

/** A divergence's imported value can be a scalar or a table-row array/object (DSN workforce
 *  distribution, countries, establishments) — never render those raw, they stringify to
 *  `[object Object]`. */
function formatImportedValue(value: unknown): string {
  if (value === null || value === undefined || value === '') {
    return __('(vide)');
  }
  if (Array.isArray(value)) {
    return `${value.length} ${__('ligne(s) importée(s)')}`;
  }
  if (typeof value === 'object') {
    const count = Object.keys(value as Record<string, unknown>).length;
    return `${count} ${__('valeur(s) importée(s)')}`;
  }
  return String(value);
}

/** Company dossier: upload artifact then run import (same REST as integration tests). */
const ProfileArtifactImportSection = ({ companyId, bootstrapKey }: ProfileArtifactImportSectionProps) => {
  const queryClient = useQueryClient();
  const fecDsnImportGate = useMemo(() => {
    try {
      return getFecDsnImportGateFromBootstrap(getBootstrapData(bootstrapKey));
    } catch {
      return getFecDsnImportGateFromBootstrap({ isFecDsnImportEnabled: false });
    }
  }, [bootstrapKey]);
  const importDisabled = !fecDsnImportGate.allowed;
  const [txtKind, setTxtKind] = useState<ProfileArtifactType>('fec_fr');
  const [stagedTxt, setStagedTxt] = useState<File | null>(null);
  const [phase, setPhase] = useState<ImportPhase>('idle');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [blockedHints, setBlockedHints] = useState<BlockedHint[] | null>(null);
  const [importOutcome, setImportOutcome] = useState<ImportEnvelope | null>(null);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [mergeReportId, setMergeReportId] = useState<number | null>(null);
  const [mergeBusy, setMergeBusy] = useState(false);
  const [mergeError, setMergeError] = useState<string | null>(null);
  const [mergeInfo, setMergeInfo] = useState<string | null>(null);
  const [mergeResult, setMergeResult] = useState<PrefillMergeResult | null>(null);

  const noDraftReportMessage = __(
    'Import terminé. Aucun rapport VSME en préparation à préremplir avec ces données.',
  );
  const mergeOfferUnavailableMessage = __(
    "Import terminé. Le préremplissage n'a pas pu être proposé automatiquement. Ouvrez le rapport VSME pour vérifier les nouvelles données.",
  );

  const runImport = async (
    file: File,
    artifactType: ProfileArtifactType,
  ): Promise<{ touched: number; conflicts: number; envelope?: ImportEnvelope }> => {
    const bootstrap = getBootstrapData(bootstrapKey);
    const base = bootstrap.restUrl.endsWith('/') ? bootstrap.restUrl : `${bootstrap.restUrl}/`;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('artifact_type', artifactType);

    setPhase('uploading');
    const up = await fetch(`${base}company/${companyId}/profile/artifacts/upload`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'X-WP-Nonce': bootstrap.nonce },
      body: formData,
    });
    if (up.status === 401) {
      throw new Error(__('Session expirée. Rechargez la page.'));
    }
    if (!up.ok) {
      throw new Error(formatError(await readWpRestError(up)));
    }
    const upJson = (await up.json()) as { artifact?: { artifact_id?: number } };
    const artifactId = upJson.artifact?.artifact_id;
    if (typeof artifactId !== 'number' || artifactId <= 0) {
      throw new Error(__('Réponse de téléversement invalide.'));
    }

    setPhase('importing');
    // Same route, headers and body as before the move; a refusal throws with the server's message.
    const body = await createFileStoreApi({ apiBase: base, nonce: bootstrap.nonce }).importArtifact(companyId, artifactId);
    const summary = body.summary ?? {};
    const touched = summary.observations_touched ?? 0;
    const conflicts = summary.conflicting_field_count ?? 0;
    const envelope: ImportEnvelope = {
      stable: body.stable,
      monthly_facts: body.monthly_facts,
      annual_facts: body.annual_facts,
      blocked: body.blocked,
    };

    return { touched, conflicts, envelope };
  };

  const offerPostImportMerge = async () => {
    const boot = getBootstrapData(bootstrapKey) as { restUrlVsme?: string };
    if (!boot.restUrlVsme) {
      setMergeInfo(mergeOfferUnavailableMessage);
      return;
    }
    try {
      const rep = await apiRequest<CompanyReportsResponse>(`company/${companyId}/reports`, {}, bootstrapKey);
      const draft = rep.items.filter(
        r => r.lifecycle_state === 'draft' || r.lifecycle_state === 'generating',
      );
      if (draft.length > 0) {
        setMergeReportId(draft[0].id);
        setShowMergeModal(true);
        setMergeError(null);
        setMergeInfo(null);
      } else {
        setMergeInfo(noDraftReportMessage);
      }
    } catch {
      // Reports fetch failed: the fact "no draft report" is unknown here, so don't claim it —
      // tell the user prefill could not be offered instead of leaving the flow silently unresolved.
      setMergeInfo(mergeOfferUnavailableMessage);
    }
  };

  const confirmPostImportMerge = async () => {
    const boot = getBootstrapData(bootstrapKey) as { restUrl: string; restUrlVsme?: string; nonce: string };
    if (!boot.restUrlVsme || mergeReportId === null) {
      return;
    }
    setMergeBusy(true);
    setMergeError(null);
    let result: PrefillMergeResult;
    try {
      result = await createFileStoreApi({ apiBase: boot.restUrl, nonce: boot.nonce })
        .prefillMerge(boot.restUrlVsme, mergeReportId);
    } catch (err) {
      setMergeError(err instanceof Error ? err.message : String(err));
      setMergeBusy(false);
      return;
    }
    try {
      setShowMergeModal(false);
      setMergeReportId(null);
      setMergeResult(result);
      await queryClient.invalidateQueries({ queryKey: ['company-reports', companyId] });
    } finally {
      setMergeBusy(false);
    }
  };

  const finishOk = async (touched: number, conflicts: number, envelope?: ImportEnvelope) => {
    const hasBlocked = hasHardImportBlock(envelope?.blocked);
    let msg = hasBlocked
      ? __('Import partiel.') + ` ${touched} ${__('champ(s) de profil mis à jour.')}`
      : __('Import terminé.') + ` ${touched} ${__('champ(s) mis à jour.')}`;
    if (conflicts > 0) {
      msg += ` ${conflicts} ${__('conflit(s) à traiter dans le profil.')}`;
    }
    setSuccess(msg);
    setBlockedHints(envelope?.blocked?.length ? envelope.blocked : null);
    setImportOutcome(envelope ?? null);
    setPhase('success');
    await queryClient.invalidateQueries({ queryKey: ['company', companyId] });
    await offerPostImportMerge();
  };

  const importFilesSequentially = async (
    files: File[],
    artifactTypes: ProfileArtifactType[],
  ): Promise<void> => {
    let totalTouched = 0;
    let totalConflicts = 0;
    let lastEnvelope: ImportEnvelope | undefined;
    for (let i = 0; i < files.length; i += 1) {
      const { touched, conflicts, envelope } = await runImport(files[i], artifactTypes[i]);
      totalTouched += touched;
      totalConflicts += conflicts;
      lastEnvelope = envelope;
    }
    await finishOk(totalTouched, totalConflicts, lastEnvelope);
  };

  const onFileChosen = async (e: ChangeEvent<HTMLInputElement>) => {
    if (importDisabled) {
      e.target.value = '';
      return;
    }
    const list = e.target.files;
    e.target.value = '';
    if (!list?.length) {
      return;
    }
    const files = Array.from(list);
    setError(null);
    setSuccess(null);
    setBlockedHints(null);
    setImportOutcome(null);
    setMergeInfo(null);
    setMergeResult(null);
    setPhase('idle');
    setStagedTxt(null);

    try {
      const detections = await Promise.all(files.map(file => detectProfileArtifactType(file)));

      if (files.length > 1) {
        const artifactTypes: ProfileArtifactType[] = [];
        for (let i = 0; i < detections.length; i += 1) {
          const detection = detections[i];
          if (detection.kind === 'certain') {
            artifactTypes.push(detection.artifactType);
            continue;
          }
          throw new Error(
            __(
              'Impossible de reconnaître le format de tous les fichiers. Utilisez une extension (.fec, .dsn, …) ou un contenu FEC / DSN reconnaissable (en-tête FEC ou rubrique DSN en première ligne utile).',
            ),
          );
        }
        await importFilesSequentially(files, artifactTypes);
        return;
      }

      const file = files[0];
      const detection = detections[0];
      if (detection.kind === 'certain') {
        await importFilesSequentially([file], [detection.artifactType]);
        return;
      }

      setStagedTxt(file);
      if (detection.kind === 'ambiguous' && detection.artifactTypes.includes('dsn_fr')) {
        setTxtKind('dsn_fr');
      } else {
        setTxtKind('fec_fr');
      }
    } catch (err) {
      setPhase('error');
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const onConfirmStagedTxt = async () => {
    if (!stagedTxt) {
      return;
    }
    setError(null);
    setSuccess(null);
    setBlockedHints(null);
    setImportOutcome(null);
    setMergeInfo(null);
    setMergeResult(null);
    try {
      const { touched, conflicts, envelope } = await runImport(stagedTxt, txtKind);
      setStagedTxt(null);
      await finishOk(touched, conflicts, envelope);
    } catch (err) {
      setPhase('error');
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const cancelStaged = () => {
    setStagedTxt(null);
    setError(null);
  };

  const busy = phase === 'uploading' || phase === 'importing';
  const primaryBusy = phase === 'uploading' ? __('Téléversement…') : phase === 'importing' ? __('Import…') : __('Importer un fichier');
  const controlsDisabled = importDisabled || busy;
  const outcomeSummary = useMemo(
    () =>
      importOutcome
        ? summarizeImportOutcome(importOutcome.monthly_facts, importOutcome.annual_facts)
        : null,
    [importOutcome],
  );

  return (
    <div className="profile-artifact-import info-tab__artifact-import">
      <FeatureGateNotice decision={fecDsnImportGate} />
      <div className="form-container form-container--wide form-field">
        <label htmlFor="profile-artifact-import-file">{__('FEC / DSN')}</label>
        <FecDsnUploadResponsibilityNotice />
        {stagedTxt && !importDisabled ? (
          <div className="profile-artifact-import__ambiguous" style={{ maxWidth: '28rem' }}>
            <p>
              <strong>{stagedTxt.name}</strong>
            </p>
            <label htmlFor="profile-artifact-txt-kind">{__('Fichier .txt : précisez le format')}</label>
            <select
              id="profile-artifact-txt-kind"
              value={txtKind}
              onChange={e => setTxtKind(e.target.value as ProfileArtifactType)}
              disabled={controlsDisabled}
            >
              <option value="fec_fr">{__('FEC (France)')}</option>
              <option value="dsn_fr">{__('DSN (France)')}</option>
            </select>
            <div className="form-actions" style={{ marginTop: '0.75rem' }}>
              <button type="button" className="btn-primary" disabled={controlsDisabled} onClick={() => void onConfirmStagedTxt()}>
                {busy ? __('Import…') : __('Importer')}
              </button>
              <button type="button" className="btn-secondary" disabled={controlsDisabled} onClick={cancelStaged}>
                {__('Annuler')}
              </button>
            </div>
          </div>
        ) : (
          <FeatureGateTooltip decision={fecDsnImportGate}>
            <ProfileArtifactFileDropzone
              inputId="profile-artifact-import-file"
              accept={COMPANY_PROFILE_ARTIFACT_FILE_ACCEPT}
              busy={busy}
              disabled={importDisabled}
              multiple
              ariaLabel={__('Choisir un ou plusieurs fichiers FEC, CSV, TXT ou DSN')}
              primaryLabel={busy ? primaryBusy : __('Importer un ou plusieurs fichiers')}
              onChange={e => void onFileChosen(e)}
            />
          </FeatureGateTooltip>
        )}
      </div>
      {error ? (
        <p className="notice notice-error" role="alert" style={{ marginTop: '0.75rem' }} data-testid="import-error">
          {error}
        </p>
      ) : null}
      {success ? (
        <p
          className={`notice ${hasHardImportBlock(blockedHints) ? 'notice-warning' : 'notice-success'}`}
          aria-live="polite"
          style={{ marginTop: '0.75rem' }}
          data-testid="import-success"
        >
          {success}
        </p>
      ) : null}
      {outcomeSummary && (outcomeSummary.monthlyCount > 0 || outcomeSummary.annualCount > 0) ? (
        <div className="notice notice-info" style={{ marginTop: '0.75rem' }} data-testid="import-outcome-summary">
          <p style={{ margin: '0 0 0.25rem' }}>
            <strong>{__('Données importées pour le préremplissage VS')}</strong>
          </p>
          <p style={{ margin: 0 }}>
            {outcomeSummary.monthlyCount} {__('agrégat(s) mensuel(s)')},{' '}
            {outcomeSummary.annualCount} {__('agrégat(s) annuel(s) ou fiscal(aux)')}
          </p>
          {outcomeSummary.factLines.length > 0 ? (
            <ul style={{ margin: '0.5rem 0 0 1rem' }}>
              {outcomeSummary.factLines.map(line => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          ) : null}
          {outcomeSummary.reviewNeededMetricKeys.length > 0 ? (
            <p style={{ margin: '0.5rem 0 0' }}>
              {__(
                'Certaines valeurs sont des estimations ou des données partielles : elles devront être validées dans le rapport VSME.',
              )}
            </p>
          ) : null}
        </div>
      ) : null}
      {blockedHints && blockedHints.length > 0 ? (
        <div
          className={`notice ${hasHardImportBlock(blockedHints) ? 'notice-warning' : 'notice-info'}`}
          style={{ marginTop: '0.75rem' }}
          data-testid="import-blocked-hints"
        >
          <p>
            <strong>
              {hasHardImportBlock(blockedHints)
                ? __('Préremplissage partiel ou bloqué')
                : __('Agrégats annuels DSN en attente')}
            </strong>
          </p>
          <ul style={{ margin: '0.25rem 0 0 1rem' }}>
            {blockedHints.map((b, i) => (
              <li key={`${b.reason_code ?? 'r'}-${i}`}>
                {b.message ?? b.reason_code ?? b.metric_key ?? ''}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {mergeInfo ? (
        <p
          className="notice notice-info"
          aria-live="polite"
          style={{ marginTop: '0.75rem' }}
          data-testid="prefill-merge-info"
        >
          {mergeInfo}
        </p>
      ) : null}
      {mergeResult ? (
        mergeResult.prefill_unavailable ? (
          <p
            className="notice notice-warning"
            role="alert"
            style={{ marginTop: '0.75rem' }}
            data-testid="prefill-merge-result"
          >
            {__(
              'Préremplissage indisponible : les données du profil entreprise n’ont pas pu être lues. Rien n’a été modifié dans le rapport.',
            )}
          </p>
        ) : (
          (() => {
            const mergedCount = mergeResult.merged_field_ids?.length ?? 0;
            const skippedNonEmpty = mergeResult.skipped_non_empty ?? 0;
            const skippedLocked = mergeResult.skipped_locked ?? 0;
            const skippedPristine = mergeResult.skipped_pristine_imported ?? 0;
            const divergences = mergeResult.divergences ?? [];
            const noticeClass = mergedCount > 0 ? 'notice-success' : 'notice-info';
            return (
              <div className={`notice ${noticeClass}`} style={{ marginTop: '0.75rem' }} data-testid="prefill-merge-result">
                <p style={{ margin: 0 }}>
                  {`${mergedCount} ${__('champ(s) prérempli(s)')}, ${skippedNonEmpty} ${__(
                    'champ(s) déjà renseigné(s) conservé(s)',
                  )}`}
                </p>
                {skippedLocked > 0 ? (
                  <p style={{ margin: '0.25rem 0 0' }}>
                    {`${skippedLocked} ${__('champ(s) verrouillé(s), non modifié(s)')}`}
                  </p>
                ) : null}
                {skippedPristine > 0 ? (
                  <p style={{ margin: '0.25rem 0 0' }}>
                    {`${skippedPristine} ${__('champ(s) déjà importé(s), inchangé(s)')}`}
                  </p>
                ) : null}
                {divergences.length > 0 ? (
                  <>
                    <p style={{ margin: '0.5rem 0 0' }}>
                      {__('Valeurs importées différentes de vos réponses actuelles, conservées telles quelles :')}
                    </p>
                    <ul style={{ margin: '0.25rem 0 0 1rem' }}>
                      {divergences.map((d, i) => (
                        <li key={`${d.field_id ?? 'field'}-${i}`}>
                          {`${divergenceFieldLabel(d)} : ${__('valeur importée')} « ${formatImportedValue(
                            d.imported_value,
                          )} »`}
                        </li>
                      ))}
                    </ul>
                  </>
                ) : null}
              </div>
            );
          })()
        )
      ) : null}
      {showMergeModal && mergeReportId !== null ? (
        <div
          className="profile-artifact-import__modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="prefill-merge-modal-title"
          data-testid="prefill-merge-modal"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.35)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100000,
          }}
        >
          <div
            className="profile-artifact-import__modal"
            style={{
              background: '#fff',
              padding: '1.25rem',
              maxWidth: '26rem',
              borderRadius: '4px',
              boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
            }}
          >
            <p id="prefill-merge-modal-title" style={{ marginTop: 0 }}>
              {__(
                'De nouvelles données sont disponibles depuis l’import. Les appliquer au rapport VSME en cours ?',
              )}
            </p>
            <div className="form-actions" style={{ marginTop: '1rem' }}>
              <button
                type="button"
                className="btn-primary"
                disabled={mergeBusy}
                onClick={() => void confirmPostImportMerge()}
              >
                {mergeBusy ? __('Application…') : __('Oui')}
              </button>
              <button
                type="button"
                className="btn-secondary"
                disabled={mergeBusy}
                onClick={() => {
                  setShowMergeModal(false);
                  setMergeReportId(null);
                  setMergeError(null);
                }}
              >
                {__('Non')}
              </button>
            </div>
            {mergeError ? (
              <p className="notice notice-error" style={{ marginTop: '0.75rem' }} role="alert">
                {mergeError}
              </p>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ProfileArtifactImportSection;
